import { memo, useId } from 'react';
import { Area, AreaChart, ResponsiveContainer } from 'recharts';
import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react';

import { useI18n } from '../../i18n';
import { formatPercent } from '../../utils';
import { CountUp } from './CountUp';
import { Skeleton } from './States';

export const Delta = ({ value }) => {
  if (value == null || !Number.isFinite(value)) return null;
  const rounded = Math.round(value * 1000) / 1000;
  const direction = rounded > 0 ? 'up' : rounded < 0 ? 'down' : 'flat';
  const Icon = direction === 'up' ? ArrowUpRight : direction === 'down' ? ArrowDownRight : Minus;
  return (
    <span className={`wm-delta wm-delta--${direction}`}>
      <Icon size={13} strokeWidth={2.25} />
      {formatPercent(Math.abs(rounded))}
    </span>
  );
};

const Sparkline = memo(({ data, color }) => {
  const id = useId().replace(/:/g, '');
  if (!data || data.length < 2) return null;
  return (
    <div className='wm-kpi__spark' aria-hidden='true'>
      <ResponsiveContainer width='100%' height='100%'>
        <AreaChart data={data} margin={{ top: 2, right: 0, bottom: 2, left: 0 }}>
          <defs>
            <linearGradient id={`spark-${id}`} x1='0' y1='0' x2='0' y2='1'>
              <stop offset='0%' stopColor={color} stopOpacity={0.28} />
              <stop offset='100%' stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area
            type='monotone'
            dataKey='v'
            stroke={color}
            strokeWidth={1.75}
            fill={`url(#spark-${id})`}
            isAnimationActive
            animationDuration={700}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
});

export const KpiCard = ({
  label,
  icon: Icon,
  tone = 'accent',
  value,
  format,
  unit,
  delta,
  deltaLabel,
  hint,
  spark,
  sparkColor = 'var(--wm-accent)',
  loading = false,
  style,
}) => {
  const { t } = useI18n();
  return (
    <div className='wm-card wm-card--interactive wm-kpi wm-reveal' style={style}>
      <div className='wm-kpi__head'>
        <span className={`wm-kpi__icon${tone !== 'accent' ? ` wm-kpi__icon--${tone}` : ''}`}>
          {Icon && <Icon size={16} strokeWidth={2} />}
        </span>
        <span className='wm-kpi__label'>{label}</span>
        {!loading && <Sparkline data={spark} color={sparkColor} />}
      </div>
      {loading ? (
        <>
          <Skeleton width='60%' height={28} style={{ marginTop: 'auto' }} />
          <Skeleton width='45%' height={14} />
        </>
      ) : (
        <>
          <div className='wm-kpi__row'>
            <div className='wm-kpi__value'>
              {value == null ? t('common.noValue') : <CountUp value={value} format={format} />}
              {unit && value != null && <small>{unit}</small>}
            </div>
          </div>
          <div className='wm-kpi__foot'>
            {delta !== undefined &&
              (delta == null ? (
                <span>{t('dashboard.noComparison')}</span>
              ) : (
                <>
                  <Delta value={delta} />
                  <span>{deltaLabel}</span>
                </>
              ))}
            {hint && <span>{hint}</span>}
          </div>
        </>
      )}
    </div>
  );
};
